import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { STAGE_LABEL_KEY } from '@/lib/project-form';
import type { StageKey } from '@/lib/adapters/types';

// Quiet 行的 mono stage 字标配色(画布 1b/2c:WIP=accent-800、SHIPPED=primary-700、
// IDEA=fg-500;paused 顺同一逻辑落最弱的 fg-400)。
const QUIET_STAGE_COLOR: Record<string, string> = {
  wip: 'text-accent-800',
  shipped: 'text-primary-700',
  idea: 'text-foreground-500',
  paused: 'text-foreground-400',
};

interface QuietProjectRowProps {
  id: string;
  title: string;
  stage: string;
  time: string;
  /** 热门序号(02…);首页 Focus Quiet 行可选,Works 页可省略。 */
  index?: number;
  /** 首页 Focus:近 7 天有求反馈帖时的轻提示(与 lead 同信号,quiet 密度)。 */
  hasFeedbackRequest?: boolean;
  feedbackLabel?: string;
}

/** Quiet 产品行(画布 1b Focus / 2c Works 共用):serif 16px 标题 + dot leaders +
 *  mono stage 字标(i18n) + 右对齐时间;行自带 border-t,容器给最后一行补 border-b。 */
export default function QuietProjectRow({
  id,
  title,
  stage,
  time,
  index,
  hasFeedbackRequest = false,
  feedbackLabel,
}: QuietProjectRowProps) {
  const { t } = useTranslation();
  const normalized = stage.toLowerCase();
  const labelKey = STAGE_LABEL_KEY[normalized as StageKey];
  const stageLabel = labelKey ? t(labelKey) : stage;

  return (
    <Link
      to={`/p/${id}`}
      className="ink-row group flex items-baseline gap-0 py-[14px] -mx-2 px-2 border-t border-foreground-200/30 rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/25 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50"
    >
      {typeof index === 'number' && (
        <span
          className="shrink-0 w-7 font-mono text-[11px] font-medium tracking-[0.06em] text-foreground-300 group-hover:text-accent-500/80 transition-colors duration-200"
          aria-hidden
        >
          {String(index).padStart(2, '0')}
        </span>
      )}
      <span className="font-heading text-base font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 truncate min-w-0">
        {title}
      </span>
      <span className="dot-leaders" aria-hidden />
      {hasFeedbackRequest && feedbackLabel && (
        <span
          className="shrink-0 inline-flex items-center gap-1 mr-2.5 text-[10px] font-medium text-accent-600"
          title={feedbackLabel}
        >
          <span className="w-[4px] h-[4px] rounded-full bg-accent-500" aria-hidden />
          <span className="hidden sm:inline">{feedbackLabel}</span>
        </span>
      )}
      <span
        className={`shrink-0 font-mono text-[10px] font-medium tracking-[0.06em] mr-3 tabular-nums ${
          QUIET_STAGE_COLOR[normalized] ?? 'text-foreground-500'
        }`}
      >
        {stageLabel}
      </span>
      <span className="shrink-0 text-xs text-foreground-400 min-w-[3.25rem] text-right tabular-nums">
        {time}
      </span>
    </Link>
  );
}
